import React, { FC } from 'react'
import { DialogId, DialogSchema } from '@feedyou/schema'
import cx from 'classnames'
import { useDispatch } from 'react-redux'

import { FeedbotCreatableSelect, FeedbotCreatableSelectProps } from '../../FeedbotCreatableSelect'
import { useDialogs } from '../../../../../store/dialog'
import { getSelectOptionForDialog } from './utils'
import { FormattedLabel } from './FormattedLabel'
import { useMemoizedDialogSelectData } from './hooks'
import { createNewDialog } from '../../../../../store/flow/thunks/createNewDialog'

type Props = Omit<FeedbotCreatableSelectProps, 'options' | 'value'> & {
  selectedDialogId: DialogId | null | undefined
}

export const DialogCreatableSelect: FC<Props> = ({
  selectedDialogId,
  'data-test': dataTest,
  ...props
}) => {
  const dialogs = useDialogs()
  const dispatch = useDispatch()
  const selectedDialog: DialogSchema | undefined | null = dialogs[selectedDialogId!]

  const {
    dialogOptions,
    isValidNewOption,
    inputValueMatchesDialogName,
    inputValueMatchesDialogId,
    getDialogDescriptorFromOptionValue
  } = useMemoizedDialogSelectData()

  return (
    <FeedbotCreatableSelect
      $minWidth={252}
      openMenuOnFocus={false}
      createOptionPosition="first"
      styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 99 }) }}
      placeholder="Enter existing or new dialog name"
      value={getSelectOptionForDialog(selectedDialog)}
      options={dialogOptions}
      filterOption={(option, inputValue) =>
        inputValueMatchesDialogName(inputValue, option.label) ||
        inputValueMatchesDialogId(inputValue, option.value)
      }
      isValidNewOption={isValidNewOption}
      {...props}
      onChange={(option, actionMeta) => {
        if (option?.__isNew__) {
          const { id, name } = getDialogDescriptorFromOptionValue(option.value)!
          dispatch(createNewDialog(id, { name }))
          props.onChange?.({ value: id, label: name }, actionMeta)
          return
        }

        props.onChange?.(option, actionMeta)
      }}
      formatOptionLabel={(option, { inputValue }) => (
        <FormattedLabel
          option={option}
          inputValueMatchesDialogIdButNotDialogName={
            inputValueMatchesDialogId(inputValue, option.value) &&
            !inputValueMatchesDialogName(inputValue, option.label)
          }
        />
      )}
      className={cx('e2e-select-dialog', props.className)}
      data-test={cx(dataTest, 'dialog-select')}
    />
  )
}

export type DialogSelectProps = Props
